import React, {
  createElement,
  FunctionComponent,
  useState,
  useEffect,
} from 'react';
import {
  FormWithRedirect as RaFormWithRedirect,
  SaveButton as RaSaveButton,
  useRedirect,
  useTranslate,
  useDelete,
  useNotify,
} from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box as MuiBox,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Grid as MuiGrid,
  Toolbar as MuiToolbar,
  Typography as MuiTypography,
} from '@material-ui/core';
import { throttle } from 'lodash';
import Sections from '../Sections';
import Tabs from '../Tabs';
import { Block } from '../../config/types';
import { FormProps } from './types';
import Resource from '../../models/Resource';
import useConfig from '../../hooks/useConfig';
import checkConditions from '../../helpers/conditions';
import { Button as RaButton } from 'ra-ui-materialui/lib/button';
import ConfirmationModal from '../modals/ConfirmationModal';
import { getIconComponentFromName } from '../../helpers/icons';
import { MISSIONS, USERS, PROPOSALS } from '../../constants/resources';

const FormStateListener: FunctionComponent = () => {
  useEffect(() => {
    // Detect user-driven changes only. MUI components, browser autofill, and
    // RelatedRecord's programmatic onChange can all fire 'input'/'change' DOM
    // events without user interaction. Guard with a prior pointer/keyboard
    // gesture: only mark dirty if the user has actually touched the form.
    let userHasInteracted = false;

    const reset = () => {
      userHasInteracted = false;
      (window as any).__form_dirty__ = false;
      (window as any).__charges_modified__ = false;
    };

    reset();
    // Expose reset so Edit/component.tsx onSuccess can call it after a
    // successful save. Without this, userHasInteracted stays true across the
    // SPA redirect and the next MUI-driven change event would re-set dirty.
    (window as any).__reset_form_dirty__ = reset;

    const formEl = document.getElementById('main-form');
    if (!formEl) return;

    const onUserGesture = () => {
      userHasInteracted = true;
    };
    const onValueChange = () => {
      if (userHasInteracted) (window as any).__form_dirty__ = true;
    };

    // Capture phase so the flag is set before any stopPropagation in children.
    formEl.addEventListener('pointerdown', onUserGesture, { capture: true });
    formEl.addEventListener('keydown', onUserGesture, { capture: true });
    formEl.addEventListener('input', onValueChange);
    formEl.addEventListener('change', onValueChange);

    return () => {
      formEl.removeEventListener('pointerdown', onUserGesture, {
        capture: true,
      });
      formEl.removeEventListener('keydown', onUserGesture, { capture: true });
      formEl.removeEventListener('input', onValueChange);
      formEl.removeEventListener('change', onValueChange);
      reset();
      (window as any).__reset_form_dirty__ = undefined;
      (window as any).__form_submit__ = undefined;
    };
  }, []);

  return null;
};

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
    marginTop: '24px',
  },
  spacing: {
    marginBottom: '24px',
  },
  button: {
    color: 'red',
  },
});

const Form: FunctionComponent<FormProps> = (props) => {
  const classes = useStyles();
  const t = useTranslate();
  const config = useConfig();
  const redirectTo = useRedirect();
  const [showDialog, setShowDialog] = useState(false);

  const { handleSave: handleSaveClosure, page, withRight, record } = props;

  const resource: Resource = config.getResource(props.resource as string);
  const tabs = resource.getTabs(page);
  const handleSave = handleSaveClosure(resource, record);
  const IconComponent = getIconComponentFromName('Delete');
  const notify = useNotify();

  // With large images (only in missions, users and proposals resource) the form layout size can be bugged, the following logic help to resize the form
  const triggerDynamicWidth = [MISSIONS, USERS, PROPOSALS].includes(
    resource.name
  );

  const [dynamicWidth, setDynamicWidth] = useState(
    window.innerWidth - 240 - 30
  );

  const handleResize = throttle(() => {
    // 240 and 30 represent the width of the left menu and padding
    setDynamicWidth(window.innerWidth - 240 - 30);
  }, 400);

  useEffect(() => {
    if (triggerDynamicWidth) {
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  const disabled =
    resource.deleteButton && resource.deleteButton.disabled && record
      ? checkConditions(
          {
            rules: resource.deleteButton.disabled,
            satisfy: 'ALL',
          },
          record
        )
      : false;

  // See EditPage#recordWarning: shown when the record is loaded (once saved:
  // see Edit)
  const recordWarning = page === 'edit' ? resource.recordWarning : undefined;
  const warnOnLoad = Boolean(
    recordWarning && record?.[recordWarning.ifTrue as string]
  );
  useEffect(() => {
    if (warnOnLoad && recordWarning) notify(recordWarning.message, 'warning');
    // Only on load, not again when the record is refreshed after a save
  }, [record?.id]);

  const [deleteOne] = useDelete(resource.name, record.id, null, {
    onFailure: () => {
      if (resource.name === 'milestones') {
        notify('notify.error.milestone.deleted', 'error');
      }
    },
  });

  function handleClick() {
    deleteOne();
    setShowDialog(false);
    redirectTo(
      resource.deleteButton.redirect.page,
      resource.deleteButton.redirect.basePath,
      record[resource.deleteButton.redirect.recordId]
    );
  }

  function handleOpenModal() {
    setShowDialog(true);
  }

  function handleCloseModal() {
    setShowDialog(false);
  }

  return (
    <RaFormWithRedirect
      {...props}
      save={handleSave}
      {...(props.mutators && {
        mutators: props.mutators,
      })}
      render={(formProps) => {
        (window as any).__form_submit__ = formProps.handleSubmitWithRedirect;

        return (
          <form id="main-form">
            <FormStateListener />
            <MuiGrid
              className={classes.root}
              {...(triggerDynamicWidth && {
                style: { maxWidth: `${dynamicWidth}px` },
              })}
            >
              <MuiGrid container spacing={3}>
                <MuiGrid item xs={12}>
                  <MuiTypography variant="h4">
                    {t(resource.label)}
                  </MuiTypography>
                </MuiGrid>
                <MuiGrid item xs={12} sm={7}>
                  {tabs.length === 1 ? (
                    <MuiCard key={0} className={classes.spacing}>
                      <MuiCardContent>
                        <Sections
                          sections={tabs[0].sections}
                          resource={resource}
                        />
                      </MuiCardContent>
                    </MuiCard>
                  ) : (
                    <Tabs
                      tabs={tabs}
                      renderItem={(tab) => (
                        <Sections sections={tab.sections} resource={resource} />
                      )}
                    />
                  )}
                  <MuiToolbar>
                    <MuiBox
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <RaSaveButton
                        saving={formProps.saving}
                        handleSubmitWithRedirect={
                          formProps.handleSubmitWithRedirect
                        }
                      />
                      {resource.deleteButton && (
                        <>
                          <RaButton
                            label={resource.deleteButton.label}
                            disabled={disabled}
                            onClick={handleOpenModal}
                            className={classes.button}
                            {...(IconComponent && {
                              startIcon: createElement(IconComponent),
                            })}
                          />
                          <ConfirmationModal
                            open={showDialog}
                            handleClick={handleClick}
                            handleClose={handleCloseModal}
                          >
                            {t(resource.deleteButton.confirmation.content)}
                          </ConfirmationModal>
                        </>
                      )}
                    </MuiBox>
                  </MuiToolbar>
                </MuiGrid>
                {withRight && (
                  <MuiGrid item xs={12} sm={5}>
                    {typeof resource.rightPanel !== 'undefined' &&
                      resource.rightPanel.map((block: Block, index: number) => (
                        <MuiCard key={index} className={classes.spacing}>
                          <MuiCardContent>
                            <Sections
                              sections={block.sections}
                              resource={resource}
                            />
                          </MuiCardContent>
                        </MuiCard>
                      ))}
                  </MuiGrid>
                )}
              </MuiGrid>
            </MuiGrid>
          </form>
        );
      }}
    />
  );
};

export default Form;
